import { Button } from '@/components/ui/button';
import { useNavigate, useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { Copy, ClipboardCheck, CircleAlert } from '@/components/ui/icons';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Route-level fallback rendered by React Router when ANY descendant throws
// during render / loader / action. Surfaces the raw exception text + stack +
// URL so the user/support can copy the full diagnostic; matches ErrorLog's
// copy-button idiom.
function serializeRouteError(err: unknown): {
  title: string;
  body: string;
} {
  // React Router's data routes throw a thrown Response sometimes; pull
  // status/statusText off it when present.
  if (
    err &&
    typeof err === 'object' &&
    'status' in err &&
    'statusText' in err
  ) {
    const r = err as { status: unknown; statusText: unknown; data?: unknown };
    const status = String(r.status);
    const statusText = String(r.statusText);
    const data =
      r.data === undefined
        ? ''
        : typeof r.data === 'string'
          ? r.data
          : JSON.stringify(r.data, null, 2);
    return {
      title: `HTTP ${status} ${statusText}`,
      body: data,
    };
  }
  if (err instanceof Error) {
    return {
      title: `${err.name}: ${err.message}`,
      body: err.stack || err.message,
    };
  }
  if (typeof err === 'string') {
    return { title: err, body: err };
  }
  try {
    const json = JSON.stringify(err, null, 2);
    return { title: 'Unknown error', body: json };
  } catch {
    return { title: 'Unknown error', body: String(err) };
  }
}

export function ErrorView() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const { title, body } = serializeRouteError(error);
  // The full payload we put on the clipboard: title + URL + body. Pre-formatted
  // so anyone receiving the paste can immediately reproduce.
  const clipboardPayload = [
    title,
    `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
    '',
    body,
  ].join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('[ErrorView] clipboard write failed', err);
    }
  };

  return (
    // TooltipProvider wraps the whole view so the Copy-to-Clipboard Tooltip
    // below resolves its context. Without this wrapper, ErrorView itself
    // throws "Tooltip must be used within TooltipProvider" the moment any
    // child route fails — the secondary crash hides the actual error and
    // shows the user a dark blank page ("grey screen of death"). The provider
    // belongs HERE (not at the app root) because ErrorView is a route-level
    // errorElement: it can be mounted in isolation, outside any layout that
    // would supply a TooltipProvider.
    <TooltipProvider>
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-telekinesis-bg-secondary-dark px-6 py-10">
        <div className="flex items-center gap-3">
          <CircleAlert className="h-8 w-8 text-red-500" />
          <h1 className="text-2xl font-bold text-red-500">
            Oops! Something went wrong.
          </h1>
        </div>
        <p className="text-center text-telekinesis-text-secondary">
          An unexpected error occurred while loading this page. The details
          below are also captured in Sentry; copying them and sharing with us
          speeds up a fix.
        </p>

        {/* Structured error log — same visual idiom as the ErrorLogBubble overlay
          so users get a consistent "copy and report" affordance everywhere a
          failure surfaces. */}
        <div className="flex w-full max-w-3xl flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs text-red-500">{title}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 rounded-md border border-telekinesis-neutral-700 bg-black px-3 py-1.5 font-mono text-xs text-telekinesis-text-secondary transition-colors hover:border-telekinesis-neutral-500 hover:text-telekinesis-text-primary"
                  aria-label="Copy error to clipboard"
                >
                  {copied ? (
                    <ClipboardCheck className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0" />
                  )}
                  {copied ? 'Copied' : 'Copy to Clipboard'}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <span>{copied ? 'Copied' : 'Copy full error + URL'}</span>
              </TooltipContent>
            </Tooltip>
          </div>
          <pre className="max-h-96 select-text overflow-auto whitespace-pre-wrap break-words rounded-md bg-black p-3 font-mono text-xs leading-relaxed text-red-500">
            {body}
          </pre>
        </div>

        <Button onClick={() => navigate('/')}>Go to Home</Button>
      </div>
    </TooltipProvider>
  );
}
